import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CourseIndexInfo,
  SubjectChapterMiniInfo,
} from '@protos/content_management/content.common.apis_pb';
import { ModuleFilteredQuestions } from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';

// ------------------------------------------------------------

export type QuestionBankCheckItem = {
  name: string;
  id: number;
  checked: boolean;
};

export type QuestionBankCourseTopic = {
  topicId: number;
  topicNo: number;
  name: string;
};

export type QuestionBankCourseChapter = {
  chapterId: number;
  chapterNo: number;
  name: string;
  topics: QuestionBankCourseTopic[];
};

export type QuestionBankTeacherSelection = {
  chapterIds: number[];
  topicIds: number[];
};

type ChapterIdsSource = {
  chapterId: number;
  chapterNo: number;
  name: string;
  topicInfo?: { topicId: number; topicNo: number; name: string }[];
};

// ------------------------------------------------------------

export const createChapterList = (
  initialData: ModuleFilteredQuestions | undefined
) => {
  return (
    initialData?.taskMetaInfo?.chapterInfo?.chapterTopicsInfo.map((data) => {
      return {
        name: `${data.chapterNo}. ${data.name}`,
        id: data.chapterId,
        checked: true,
      };
    }) || []
  );
};

// ------------------------------------------------------------

// Every topic on the assessment payload (selected chapters only).
export const createAllTopicList = (
  initialData: ModuleFilteredQuestions | undefined
) => {
  if (!initialData) return [];

  return (
    initialData?.taskMetaInfo?.chapterInfo?.chapterTopicsInfo?.flatMap(
      (chapter) =>
        chapter.topicInfo?.map((topic) => ({
          name: topic.name,
          id: topic.topicId,
          checked: true,
        })) || []
    ) || []
  );
};

// ------------------------------------------------------------

// Lookup used to sync chapter ↔ topic selection (e.g. auto-select chapter when a topic is checked).
export const getTopicIdToChapterIdMap = (
  initialData: ModuleFilteredQuestions | undefined
) => {
  const topicIdToChapterId = new Map<number, number>();
  initialData?.taskMetaInfo?.chapterInfo?.chapterTopicsInfo?.forEach(
    (chapter) => {
      chapter.topicInfo?.forEach((topic) => {
        topicIdToChapterId.set(topic.topicId, chapter.chapterId);
      });
    }
  );
  return topicIdToChapterId;
};

// ------------------------------------------------------------

const byNumber = (a: number, b: number) => a - b;

export const courseChaptersFromMiniInfo = (
  chapters: SubjectChapterMiniInfo[] | undefined
): QuestionBankCourseChapter[] =>
  (chapters ?? []).map((chapter) => ({
    chapterId: chapter.chapterId,
    chapterNo: chapter.chapterNo,
    name: chapter.chapterTitle,
    topics: (chapter.topics ?? []).map((topic) => ({
      topicId: topic.topicId,
      topicNo: topic.topicNo,
      name: topic.topicTitle,
    })),
  }));

export const courseChaptersFromChapterIdsInfo = (
  chapters: ChapterIdsSource[] | undefined
): QuestionBankCourseChapter[] =>
  (chapters ?? []).map((chapter) => ({
    chapterId: chapter.chapterId,
    chapterNo: chapter.chapterNo,
    name: chapter.name,
    topics: (chapter.topicInfo ?? []).map((topic) => ({
      topicId: topic.topicId,
      topicNo: topic.topicNo,
      name: topic.name,
    })),
  }));

export const teacherSelectionFromChapterIdsInfo = (
  chapters: ChapterIdsSource[] | undefined
): QuestionBankTeacherSelection => ({
  chapterIds: (chapters ?? []).map((chapter) => chapter.chapterId),
  topicIds: (chapters ?? []).flatMap((chapter) =>
    (chapter.topicInfo ?? []).map((topic) => topic.topicId)
  ),
});

export const teacherSelectionFromMiniInfo = (
  chapters: SubjectChapterMiniInfo[] | undefined
): QuestionBankTeacherSelection => ({
  chapterIds: (chapters ?? []).map((chapter) => chapter.chapterId),
  topicIds: (chapters ?? []).flatMap((chapter) =>
    (chapter.topics ?? []).map((topic) => topic.topicId)
  ),
});

export const getTopicIdToChapterIdMapFromCourse = (
  chapters: QuestionBankCourseChapter[]
) => {
  const topicIdToChapterId = new Map<number, number>();
  chapters.forEach((chapter) => {
    chapter.topics.forEach((topic) => {
      topicIdToChapterId.set(topic.topicId, chapter.chapterId);
    });
  });
  return topicIdToChapterId;
};

export const buildChapterCheckboxList = (
  chapters: QuestionBankCourseChapter[],
  selectedChapterIds: Set<number>
): QuestionBankCheckItem[] =>
  [...chapters]
    .sort((a, b) => byNumber(a.chapterNo, b.chapterNo))
    .map((chapter) => ({
      name: `${chapter.chapterNo}. ${chapter.name}`,
      id: chapter.chapterId,
      checked: selectedChapterIds.has(chapter.chapterId),
    }));

// Topics of the checked chapters. With no chapter checked, every topic in the catalog is listed.
// `checkedTopicIds` marks which of those topics are selected.
export const buildVisibleTopicCheckboxList = (
  chapters: QuestionBankCourseChapter[],
  checkedChapterIds: Set<number>,
  checkedTopicIds: Set<number>
): QuestionBankCheckItem[] =>
  [...chapters]
    .filter(
      (chapter) =>
        checkedChapterIds.size === 0 || checkedChapterIds.has(chapter.chapterId)
    )
    .sort((a, b) => byNumber(a.chapterNo, b.chapterNo))
    .flatMap((chapter) =>
      [...chapter.topics]
        .sort((a, b) => byNumber(a.topicNo, b.topicNo))
        .map((topic) => ({
          name: topic.name,
          id: topic.topicId,
          checked: checkedTopicIds.has(topic.topicId),
        }))
    );

export const buildInitialQuestionBankFilters = (
  chapters: QuestionBankCourseChapter[],
  selection: QuestionBankTeacherSelection
) => {
  const selectedChapterIds = new Set(selection.chapterIds);
  return {
    chapterList: buildChapterCheckboxList(chapters, selectedChapterIds),
    topicList: buildVisibleTopicCheckboxList(
      chapters,
      selectedChapterIds,
      new Set(selection.topicIds)
    ),
  };
};

export const checkedIds = (list: QuestionBankCheckItem[]) =>
  list.filter((item) => item.checked).map((item) => item.id);

export const checkedFilterKey = (
  chapterList: QuestionBankCheckItem[],
  topicList: QuestionBankCheckItem[]
) => `${checkedIds(chapterList).join(',')}|${checkedIds(topicList).join(',')}`;

export const originalModuleIdsFromSelection = (
  selection: QuestionBankTeacherSelection
): number[] => [...selection.chapterIds, ...selection.topicIds];

// Checked chapter ids and checked topic ids. Empty selection falls back to the teacher's original modules.
export const moduleIdsFromCheckedFilters = (
  chapterList: QuestionBankCheckItem[],
  topicList: QuestionBankCheckItem[],
  fallbackModuleIds: number[]
): number[] => {
  const chapterIds = checkedIds(chapterList);
  const topicIds = checkedIds(topicList);
  const hasChapters = chapterIds.length > 0;
  const hasTopics = topicIds.length > 0;
  if (hasChapters && !hasTopics) return chapterIds;
  if (hasChapters && hasTopics) return [...chapterIds, ...topicIds];
  if (!hasChapters && hasTopics) return topicIds;
  return fallbackModuleIds;
};

export const applyChapterCheckboxToggle = (
  chapters: QuestionBankCourseChapter[],
  chapterList: QuestionBankCheckItem[],
  topicList: QuestionBankCheckItem[],
  chapterId: number
) => {
  const isChecking = !chapterList.find((item) => item.id === chapterId)?.checked;
  const nextChapterList = chapterList.map((item) =>
    item.id === chapterId ? { ...item, checked: !item.checked } : item
  );
  const checkedChapterIds = new Set(checkedIds(nextChapterList));
  const previouslyCheckedTopicIds = new Set(checkedIds(topicList));
  const toggledTopicIds = new Set(
    (
      chapters.find((chapter) => chapter.chapterId === chapterId)?.topics ?? []
    ).map((topic) => topic.topicId)
  );
  const nextCheckedTopicIds = new Set<number>();

  previouslyCheckedTopicIds.forEach((topicId) => {
    if (!toggledTopicIds.has(topicId)) nextCheckedTopicIds.add(topicId);
  });
  if (isChecking) {
    toggledTopicIds.forEach((topicId) => nextCheckedTopicIds.add(topicId));
  }

  return {
    chapterList: nextChapterList,
    topicList: buildVisibleTopicCheckboxList(
      chapters,
      checkedChapterIds,
      nextCheckedTopicIds
    ),
  };
};

export const applyTopicCheckboxToggle = (
  chapters: QuestionBankCourseChapter[],
  chapterList: QuestionBankCheckItem[],
  topicList: QuestionBankCheckItem[],
  topicId: number
) => {
  const isChecking = !topicList.find((item) => item.id === topicId)?.checked;
  const parentChapterId = getTopicIdToChapterIdMapFromCourse(chapters).get(
    topicId
  );
  const nextChapterList =
    isChecking &&
    parentChapterId !== undefined &&
    !chapterList.some((chapter) => chapter.id === parentChapterId && chapter.checked)
      ? chapterList.map((chapter) =>
          chapter.id === parentChapterId
            ? { ...chapter, checked: true }
            : chapter
        )
      : chapterList;
  const nextCheckedTopicIds = new Set(checkedIds(topicList));
  if (isChecking) nextCheckedTopicIds.add(topicId);
  else nextCheckedTopicIds.delete(topicId);

  return {
    chapterList: nextChapterList,
    topicList: buildVisibleTopicCheckboxList(
      chapters,
      new Set(checkedIds(nextChapterList)),
      nextCheckedTopicIds
    ),
  };
};

export const clearChapterChecks = (
  chapterList: QuestionBankCheckItem[],
  chapters: QuestionBankCourseChapter[]
) => ({
  chapterList: chapterList.map((item) => ({ ...item, checked: false })),
  topicList: buildVisibleTopicCheckboxList(chapters, new Set(), new Set()),
});

export const clearTopicChecks = (topicList: QuestionBankCheckItem[]) =>
  topicList.map((item) => ({ ...item, checked: false }));

// ------------------------------------------------------------

type FetchCourseIndexInfo = (request: {
  personId: bigint;
  personType: ProfileRolesEnum;
  subjectId: number;
}) => Promise<{ data?: CourseIndexInfo }>;

// Full subject catalog for the question-bank filters, with the teacher's chapters and topics pre-checked.
export const useQuestionBankChapterFilters = ({
  personId,
  subjectId,
  fetchCourseIndexInfo,
  fallbackChapterInfo,
  selection,
  resetKey,
}: {
  personId?: bigint | number | string;
  subjectId?: number;
  fetchCourseIndexInfo: FetchCourseIndexInfo;
  fallbackChapterInfo: ChapterIdsSource[] | undefined;
  selection: QuestionBankTeacherSelection;
  resetKey: string;
}) => {
  const selectionKey = `${selection.chapterIds.join(',')}|${selection.topicIds.join(
    ','
  )}`;
  const stableSelection = useMemo(
    () => selection,
    // selectionKey captures the ids; the object identity changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectionKey]
  );
  const fallbackKey = (fallbackChapterInfo ?? [])
    .map(
      (chapter) =>
        `${chapter.chapterId}:${(chapter.topicInfo ?? [])
          .map((topic) => topic.topicId)
          .join(',')}`
    )
    .join('|');
  const fallbackCatalog = useMemo(
    () => courseChaptersFromChapterIdsInfo(fallbackChapterInfo),
    // fallbackKey captures chapter and topic ids.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fallbackKey]
  );
  const [courseState, setCourseState] = useState<{
    key: string;
    chapters: QuestionBankCourseChapter[];
  } | null>(null);
  const userEditedRef = useRef(false);
  const initializedRef = useRef(false);
  const initialFilters = buildInitialQuestionBankFilters(
    fallbackCatalog,
    stableSelection
  );
  const [chapterList, setChapterList] = useState(initialFilters.chapterList);
  const [topicList, setTopicList] = useState(initialFilters.topicList);
  const fetchRef = useRef(fetchCourseIndexInfo);
  fetchRef.current = fetchCourseIndexInfo;
  const prevResetKey = useRef(resetKey);
  const courseChapters =
    courseState?.key === resetKey ? courseState.chapters : null;

  const catalog = courseChapters?.length ? courseChapters : fallbackCatalog;

  useEffect(() => {
    if (prevResetKey.current === resetKey) return;
    prevResetKey.current = resetKey;
    userEditedRef.current = false;
    initializedRef.current = false;
    const next = buildInitialQuestionBankFilters(
      fallbackCatalog,
      stableSelection
    );
    setChapterList(next.chapterList);
    setTopicList(next.topicList);
  }, [resetKey, fallbackCatalog, stableSelection]);

  useEffect(() => {
    if (personId == null || personId === '' || subjectId == null) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetchRef.current({
          personId: BigInt(personId),
          personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
          subjectId,
        });
        if (cancelled || !response.data?.chapters?.length) return;
        setCourseState({
          key: resetKey,
          chapters: courseChaptersFromMiniInfo(response.data.chapters),
        });
      } catch {
        // Keep the assessment chapter list until the course index is available.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [personId, subjectId, resetKey]);

  useEffect(() => {
    if (userEditedRef.current) return;
    const source = courseChapters?.length ? courseChapters : fallbackCatalog;
    if (!source.length) return;
    // Question refetches can change the assessment payload. Don't rebuild from that
    // after the first list is shown; still replace it when the full course index arrives.
    if (initializedRef.current && !courseChapters?.length) return;
    initializedRef.current = true;
    const next = buildInitialQuestionBankFilters(source, stableSelection);
    setChapterList(next.chapterList);
    setTopicList(next.topicList);
  }, [courseChapters, fallbackCatalog, stableSelection]);

  const markFiltersEdited = () => {
    userEditedRef.current = true;
  };

  return {
    catalog,
    chapterList,
    setChapterList,
    topicList,
    setTopicList,
    markFiltersEdited,
  };
};
